import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  TextField,
  CircularProgress,
  Container,
  Select,
  MenuItem as MuiMenuItem,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useSnackbar } from '../contexts/SnackbarContext.tsx';
import { siteConfigApi, MenuItem } from '../services/siteConfigService.ts';
import { dailySpecialsPrintService } from '../services/printService.ts';

const DAY_OPTIONS = ['Everyday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// Category sections, each kept out of the day-grouped Entrées table below. Appetizers
// and Soup print/display above Entrées; Promo and Desserts stay below it.
const SECTIONS_BEFORE_ENTREES: { category: string; title: string; addLabel: string; emptyMessage: string; showDay: boolean }[] = [
  { category: 'appetizer', title: 'Appetizers', addLabel: 'Add Appetizer', emptyMessage: 'No appetizers yet. Click "Add Appetizer" to add one.', showDay: true },
  { category: 'soup', title: 'Soup', addLabel: 'Add Soup', emptyMessage: 'No soups yet. Click "Add Soup" to add one.', showDay: true },
];
const SECTIONS_AFTER_ENTREES: { category: string; title: string; addLabel: string; emptyMessage: string; showDay: boolean }[] = [
  { category: 'promo', title: 'Promo', addLabel: 'Add Promo', emptyMessage: 'No promos yet. Click "Add Promo" to add one.', showDay: true },
  { category: 'dessert', title: 'Desserts', addLabel: 'Add Dessert', emptyMessage: 'No desserts yet. Click "Add Dessert" to add one.', showDay: true },
];
const EXTRA_SECTION_CATEGORIES = [...SECTIONS_BEFORE_ENTREES, ...SECTIONS_AFTER_ENTREES].map((s) => s.category);

const DailySpecialsPage: React.FC = () => {
  const { sessionToken } = useAuth();
  const { showError, showSuccess } = useSnackbar();
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'pending' | 'saving' | 'saved' | 'error'>('idle');
  const [autoSaveError, setAutoSaveError] = useState('');
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    fetchDailySpecials();
  }, []);

  const fetchDailySpecials = async () => {
    setLoading(true);
    try {
      const response = await siteConfigApi.getDailySpecial();
      if (response.jsonData?.menu_items) {
        setMenuItems(response.jsonData.menu_items);
      } else {
        setMenuItems([]);
      }
    } catch (err) {
      // If 404, it means no data exists yet, which is fine
      if (err instanceof Error && err.message.includes('404')) {
        setMenuItems([]);
      } else {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch daily specials';
        showError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // Auto-saves ~900ms after the last change to any row (add, edit, or delete). There's
  // no separate create/edit mode here - the whole list is always one saved document.
  // Returns an error message on failure, or null on success, so callers (like the
  // per-row Update button) can tell whether the save actually went through.
  const performAutoSave = useCallback(
    async (snapshot: MenuItem[]): Promise<string | null> => {
      if (!sessionToken) return 'You must be logged in to save.';
      const hasEmptyRequiredFields = snapshot.some((item) => !item.name.trim() || !item.price.trim());
      if (hasEmptyRequiredFields) {
        const message = 'Please fill in name and price for all items.';
        setAutoSaveStatus('error');
        setAutoSaveError(message);
        return message;
      }
      setAutoSaveStatus('saving');
      try {
        await siteConfigApi.saveDailySpecial({ menu_items: snapshot }, sessionToken);
        setAutoSaveStatus('saved');
        return null;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to save changes';
        setAutoSaveStatus('error');
        setAutoSaveError(message);
        return message;
      }
    },
    [sessionToken]
  );

  const scheduleAutoSave = (next: MenuItem[]) => {
    if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    setAutoSaveStatus('pending');
    autoSaveTimeoutRef.current = setTimeout(() => {
      autoSaveTimeoutRef.current = null;
      performAutoSave(next);
    }, 900);
  };

  const handleAddRow = (category: string = '') => {
    const next = [...menuItems, { name: '', description: '', price: '', day: '', category }];
    setMenuItems(next);
    // Don't auto-save yet - a freshly added row has no name/price, so it would just error.
  };

  const handleDeleteRow = (index: number) => {
    const next = menuItems.filter((_, i) => i !== index);
    setMenuItems(next);
    scheduleAutoSave(next);
  };

  const handleFieldChange = (index: number, field: keyof MenuItem, value: string) => {
    const next = [...menuItems];
    next[index] = { ...next[index], [field]: value };
    setMenuItems(next);
    scheduleAutoSave(next);
  };

  // Rows auto-save as you type, but this lets a row be confirmed/saved immediately
  // instead of waiting out the debounce, with its own explicit confirmation.
  const handleUpdateRow = async (index: number) => {
    const item = menuItems[index];
    if (!item.name.trim() || !item.price.trim()) {
      showError('Please fill in name and price before updating.');
      return;
    }
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }
    const errorMessage = await performAutoSave(menuItems);
    if (errorMessage) {
      showError(errorMessage);
    } else {
      showSuccess('Item updated.');
    }
  };

  const handleSave = async () => {
    if (!sessionToken) {
      showError('You must be logged in to save');
      return;
    }

    // Validate that required fields (name and price) are filled for any existing items
    if (menuItems.length > 0) {
      const hasEmptyRequiredFields = menuItems.some(
        (item) => !item.name.trim() || !item.price.trim()
      );

      if (hasEmptyRequiredFields) {
        showError('Please fill in name and price for all items before saving');
        return;
      }
    }

    setSaving(true);
    try {
      await siteConfigApi.saveDailySpecial({ menu_items: menuItems }, sessionToken);
      showSuccess('Daily specials saved successfully!');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save daily specials';
      showError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = () => {
    if (menuItems.length === 0) {
      showError('Add at least one special before generating the template.');
      return;
    }
    try {
      dailySpecialsPrintService.openPrintDialog(menuItems);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate the specials template';
      showError(errorMessage);
    }
  };

  // Keep track of each item's real index in menuItems so edits/deletes still target the right entry
  const indexedItems = menuItems.map((item, index) => ({ item, index }));
  const foodEntries = indexedItems.filter(({ item }) => !EXTRA_SECTION_CATEGORIES.includes(item.category || ''));

  const renderItemsTable = (
    entries: { item: MenuItem; index: number }[],
    { showDay, emptyMessage }: { showDay: boolean; emptyMessage: string }
  ) => {
    const columns = showDay
      ? [
          { label: 'Day', w: '15%' },
          { label: 'Name', w: '25%' },
          { label: 'Description (Optional)', w: '35%' },
          { label: 'Price', w: '15%' },
          { label: 'Actions', w: '10%' },
        ]
      : [
          { label: 'Name', w: '30%' },
          { label: 'Description (Optional)', w: '40%' },
          { label: 'Price', w: '20%' },
          { label: 'Actions', w: '10%' },
        ];

    return (
      <TableContainer component={Paper} sx={{ border: '2px solid #9e9e9e' }}>
        <Table sx={{ borderCollapse: 'collapse', '& td': { border: '2px solid #9e9e9e', py: 1.25, px: 1.5 } }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#b71c1c' }}>
              {columns.map(({ label, w }) => (
                <TableCell key={label} sx={{
                  color: '#fff', fontWeight: 700, fontSize: '0.82rem', letterSpacing: '0.04em',
                  textTransform: 'uppercase', border: '2px solid #7f0000', py: 1.5, px: 1.5, width: w,
                }}>
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 5, border: '2px solid #9e9e9e', color: 'text.secondary' }}>
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              entries.map(({ item, index }, rowIdx) => (
                <TableRow key={index} sx={{ backgroundColor: rowIdx % 2 === 0 ? '#fff' : '#fdf5f5', '&:hover': { backgroundColor: '#fce4e4' } }}>
                  {showDay && (
                    <TableCell>
                      <Select fullWidth size="small" displayEmpty value={item.day || ''}
                        onChange={(e) => handleFieldChange(index, 'day', e.target.value)}
                        variant="outlined">
                        <MuiMenuItem value=""><em>None</em></MuiMenuItem>
                        {DAY_OPTIONS.map((day) => (
                          <MuiMenuItem key={day} value={day}>{day}</MuiMenuItem>
                        ))}
                      </Select>
                    </TableCell>
                  )}
                  <TableCell>
                    <TextField fullWidth size="small" value={item.name}
                      onChange={(e) => handleFieldChange(index, 'name', e.target.value)}
                      placeholder="Menu item name" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <TextField fullWidth size="small" value={item.description}
                      onChange={(e) => handleFieldChange(index, 'description', e.target.value)}
                      placeholder="Description (optional)" variant="outlined" multiline maxRows={3} />
                  </TableCell>
                  <TableCell>
                    <TextField fullWidth size="small" value={item.price.replace(/^\$\s*/, '')}
                      onChange={(e) => handleFieldChange(index, 'price', e.target.value)}
                      placeholder="0.00" variant="outlined"
                      InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography> }} />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Update now">
                      <IconButton color="primary" onClick={() => handleUpdateRow(index)} size="small">
                        <SaveIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton color="error" onClick={() => handleDeleteRow(index)} size="small">
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ color: "#7f0000", fontWeight: 700, flex: "0 0 auto", whiteSpace: "nowrap" }}>
          Date: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
        </Typography>
        <Typography variant="h4" component="h1" sx={{ flex: 1, textAlign: 'center', fontWeight: 700, textTransform: 'uppercase' }}>
          Daily Specials
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography
            variant="body2"
            color={autoSaveStatus === 'error' ? 'error' : 'text.secondary'}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            {autoSaveStatus === 'saving' && (<><CircularProgress size={14} />Saving…</>)}
            {autoSaveStatus === 'pending' && 'Unsaved changes…'}
            {autoSaveStatus === 'saved' && 'All changes saved'}
            {autoSaveStatus === 'error' && (autoSaveError || 'Failed to save changes')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handleGenerate}
            sx={{ backgroundColor: '#b71c1c', '&:hover': { backgroundColor: '#7f0000' } }}
          >
            Generate Flyer
          </Button>
        </Box>
      </Box>

      {SECTIONS_BEFORE_ENTREES.map(({ category, title, addLabel, emptyMessage, showDay }) => {
        const entries = indexedItems.filter(({ item }) => item.category === category);
        return (
          <React.Fragment key={category}>
            <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">{title}</Typography>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={() => handleAddRow(category)}>
                {addLabel}
              </Button>
            </Box>
            {renderItemsTable(entries, { showDay, emptyMessage })}
          </React.Fragment>
        );
      })}

      <Box sx={{ mt: 4, mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Entrées</Typography>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => handleAddRow()}>
          Add Entrée
        </Button>
      </Box>
      {renderItemsTable(foodEntries, { showDay: true, emptyMessage: 'No entrées yet. Click "Add Entrée" to add one.' })}

      {SECTIONS_AFTER_ENTREES.map(({ category, title, addLabel, emptyMessage, showDay }) => {
        const entries = indexedItems.filter(({ item }) => item.category === category);
        return (
          <React.Fragment key={category}>
            <Box sx={{ mt: 4, mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">{title}</Typography>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={() => handleAddRow(category)}>
                {addLabel}
              </Button>
            </Box>
            {renderItemsTable(entries, { showDay, emptyMessage })}
          </React.Fragment>
        );
      })}
    </Container>
  );
};

export default DailySpecialsPage;

